import { useState, useCallback } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://jetscanner360.com/api/v1';

export const useRefund = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refundStatus, setRefundStatus] = useState(null);
  const [pendingRefunds, setPendingRefunds] = useState([]);

  /**
   * Request refund for a booking
   */
  const requestRefund = useCallback(async (bookingToken, reason) => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.post(
        `${API_BASE_URL}/refunds/request/${bookingToken}`,
        { reason },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message
        };
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to request refund';
      setError(errorMessage);
      return {
        success: false,
        message: errorMessage
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get refund status for a booking
   */
  const getRefundStatus = useCallback(async (bookingToken) => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `${API_BASE_URL}/refunds/status/${bookingToken}`,
        {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        setRefundStatus(response.data.data);
        return response.data.data;
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to get refund status';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get pending refunds (Admin)
   */
  const getPendingRefunds = useCallback(async (page = 1, limit = 20) => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `${API_BASE_URL}/refunds/admin/pending`,
        {
          params: { page, limit },
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        setPendingRefunds(response.data.data.refund_requests);
        return response.data.data;
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to get pending refunds';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Approve refund as SubAdmin
   */
  const approveRefundAsSubAdmin = useCallback(async (refundId, approvalStatus, reason, adminNotes) => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.post(
        `${API_BASE_URL}/refunds/admin/approve-subadmin/${refundId}`,
        {
          approval_status: approvalStatus,
          reason,
          admin_notes: adminNotes
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message
        };
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to process approval';
      setError(errorMessage);
      return {
        success: false,
        message: errorMessage
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Approve refund as Admin
   */
  const approveRefundAsAdmin = useCallback(async (refundId, approvalStatus, reason, adminNotes) => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.post(
        `${API_BASE_URL}/refunds/admin/approve-admin/${refundId}`,
        {
          approval_status: approvalStatus,
          reason,
          admin_notes: adminNotes
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message
        };
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to process approval';
      setError(errorMessage);
      return {
        success: false,
        message: errorMessage
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Retry Stripe/PayPal payout for an approved refund that failed (status Failed).
   */
  const retryFailedRefundPayment = useCallback(async (refundId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.post(
        `${API_BASE_URL}/refunds/admin/retry-payment/${refundId}`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${sessionStorage.getItem('token')}`
          }
        }
      );

      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to retry refund payment';
      setError(errorMessage);
      return {
        success: false,
        message: errorMessage
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get refund details with audit log
   */
  const getRefundDetails = useCallback(async (refundId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `${API_BASE_URL}/refunds/admin/details/${refundId}`,
        {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        return response.data.data;
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to get refund details';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get refund history (all refunds - pending, completed, rejected, etc.)
   */
  const getRefundHistory = useCallback(async (page = 1, limit = 20, status = null, sortOrder = 'DESC') => {
    try {
      setLoading(true);
      setError(null);

      const params = { page, limit, sortOrder };
      if (status) {
        params.status = status;
      }

      const response = await axios.get(
        `${API_BASE_URL}/refunds/admin/history`,
        {
          params,
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        return response.data.data;
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to get refund history';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    refundStatus,
    pendingRefunds,
    requestRefund,
    getRefundStatus,
    getPendingRefunds,
    approveRefundAsSubAdmin,
    approveRefundAsAdmin,
    retryFailedRefundPayment,
    getRefundDetails,
    getRefundHistory
  };
};

export default useRefund;
